import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, BellRing } from "lucide-react";
import { adminGetSettings, adminPutSettings, adminChangePassword } from "@/lib/api";
import { AdminAccountsCard } from "./AdminAccountsCard";
import { adminInputCls, adminGoldBtnCls } from "./shared";

export const SettingsTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [googleUrl, setGoogleUrl] = useState("");
  const [notif, setNotif] = useState({ email: "", on_bid: true, on_review: true });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    adminGetSettings()
      .then((r) => {
        setGoogleUrl(r.data.google_review_url || "");
        setNotif({
          email: r.data.notifications?.email || "",
          on_bid: r.data.notifications?.on_bid ?? true,
          on_review: r.data.notifications?.on_review ?? true,
        });
        setLoaded(true);
      })
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else setLoaded(true);
      });
  }, [onAuthError]);

  const saveSettings = async () => {
    setSaving("settings");
    try {
      await adminPutSettings({ google_review_url: googleUrl, notifications: notif });
      toast.success("Settings saved");
    } catch (e: any) {
      if (e?.response?.status === 401) onAuthError();
      else toast.error("Save failed");
    }
    setSaving("");
  };

  const savePassword = async () => {
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving("password");
    try {
      await adminChangePassword({ current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e: any) {
      if (e?.response?.status === 401) onAuthError();
      else toast.error(e?.response?.data?.detail || "Could not update password");
    }
    setSaving("");
  };

  if (!loaded) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-tab" className="space-y-6">
      <div className="border border-white/10 bg-charcoal p-6">
        <p className="flex items-center gap-2 font-display text-lg font-black uppercase tracking-tight text-white">
          <BellRing className="h-5 w-5 text-gold" />
          Email Notifications
        </p>
        <p className="mt-2 text-xs leading-relaxed text-neutral-500">
          Delivery is a placeholder until an email provider key is added to the server. These
          settings control who gets notified and for what, once it is active.
        </p>
        <div className="mt-4">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            Notification recipient email
          </label>
          <input
            data-testid="notif-email-input"
            type="email"
            value={notif.email}
            onChange={(e) => setNotif((n) => ({ ...n, email: e.target.value }))}
            placeholder="you@example.com"
            className={adminInputCls}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-6">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-300">
            <input
              data-testid="notif-bid-toggle"
              type="checkbox"
              checked={notif.on_bid}
              onChange={(e) => setNotif((n) => ({ ...n, on_bid: e.target.checked }))}
              className="h-4 w-4"
            />
            New bid request submitted
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-300">
            <input
              data-testid="notif-review-toggle"
              type="checkbox"
              checked={notif.on_review}
              onChange={(e) => setNotif((n) => ({ ...n, on_review: e.target.checked }))}
              className="h-4 w-4"
            />
            New review submitted
          </label>
        </div>

        <div className="mt-6">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            Google review link (shows the “Prefer Google?” section on the Reviews page once set)
          </label>
          <input
            data-testid="admin-google-url-input"
            value={googleUrl}
            onChange={(e) => setGoogleUrl(e.target.value)}
            placeholder="https://g.page/r/…"
            className={adminInputCls}
          />
        </div>
        <button data-testid="settings-save-btn" onClick={saveSettings} disabled={saving === "settings"} className={`${adminGoldBtnCls} mt-5`}>
          {saving === "settings" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Settings
        </button>
      </div>

      <AdminAccountsCard onAuthError={onAuthError} />

      <div className="border border-white/10 bg-charcoal p-6">
        <p className="flex items-center gap-2 font-display text-lg font-black uppercase tracking-tight text-white">
          <KeyRound className="h-5 w-5 text-gold" />
          Change Password
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <input
            data-testid="pw-current-input"
            type="password"
            placeholder="Current password"
            value={pw.current}
            onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
            className={adminInputCls}
          />
          <input
            data-testid="pw-new-input"
            type="password"
            placeholder="New password (8+ characters)"
            value={pw.next}
            onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
            className={adminInputCls}
          />
          <input
            data-testid="pw-confirm-input"
            type="password"
            placeholder="Confirm new password"
            value={pw.confirm}
            onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
            className={adminInputCls}
          />
        </div>
        <button data-testid="pw-save-btn" onClick={savePassword} disabled={saving === "password"} className={`${adminGoldBtnCls} mt-5`}>
          {saving === "password" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Update Password
        </button>
      </div>
    </div>
  );
};
